/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import * as React from 'react';
import {
  Box,
  createStyles,
  Grid,
  IconButton,
  Button,
  makeStyles,
  Paper,
  Tooltip, Typography,
  debounce,
  CircularProgress,
} from '@material-ui/core';
import type {
  GridColumns,
  GridCellParams,
  GridColDef,
  GridEditRowsModel,
  GridCellEditCommitParams,
} from '@material-ui/data-grid';
import {
  DataGrid,
} from '@material-ui/data-grid';
import AddIcon from '@material-ui/icons/Add';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import ErrorIcon from '@material-ui/icons/Error';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import type { AxiosError } from 'axios';
import axios from 'axios';
import SaveIcon from '@material-ui/icons/Save';
import ClearIcon from '@material-ui/icons/Clear';
import type { MutatorCallback } from 'swr/dist/types';
import { useSnackbar } from 'notistack';
import type { GridApi } from '@material-ui/x-grid';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';

import TableCellChip from '../TableCellChip';
import RepairActionMenu from './RepairActionMenu';
import EmptyBagDialog from './EmptyBagDialog';
import type { IOpenedBox } from './OpenerInterfaces';
import useEventListener from '../../lib/useEventListener';
import ManagerOverrideModal from './ManagerOverrideModal';

interface IRepairItemsScannedProps {
  openedBox: IOpenedBox;
  manuallyAddedRepairs: { barcode: string, storeRepairNumber: string, id: number }[];
  setManuallyAddedRepairs: (repairs: { barcode: string, storeRepairNumber: string, id: number }[]) => void;
  mutate: (data?: IOpenedBox | Promise<IOpenedBox> | MutatorCallback<IOpenedBox>, shouldRevlidate?: boolean)
  => Promise<IOpenedBox | undefined>;
}

const useStyles = makeStyles((theme) => createStyles({
  tableContainer: {
    boxShadow: 'none',
    border: 'none',
    '& .Mui-error': {
      backgroundColor: 'rgb(126,10,15,0.1)',
      color: '#750f0f',
    },
    '& div.Mui-even.MuiDataGrid-row.Mui-selected': {
      backgroundColor: '#4485BE',
      '&:last-child': {
        borderBottomLeftRadius: '6px',
        borderBottomRightRadius: '6px',
      },
    },
    '& div.Mui-odd.MuiDataGrid-row.Mui-selected': {
      backgroundColor: '#4485BE',
      '&:last-child': {
        borderBottomLeftRadius: '6px',
        borderBottomRightRadius: '6px',
      },
    },
  },
  title: {
    fontWeight: 700,
    fontSize: 18,
  },
  saveRepairButton: {
    minWidth: '11.25rem',
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
  noFocus: {
    '& .MuiDataGrid-root': {
      '& .MuiDataGrid-colCell:focus:not(.MuiDataGrid-cellEditable)': {
        outline: 'none',
      },
    },
    '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within:not(.MuiDataGrid-cellEditable)': {
      outline: 'none',
    },
    '& .Mui-error': {
      backgroundColor: 'rgb(126,10,15,0.1)',
      color: '#750f0f',
    },
    '& div.Mui-even.MuiDataGrid-row.Mui-selected': {
      backgroundColor: '#4485BE',
      '&:last-child': {
        borderBottomLeftRadius: '6px',
        borderBottomRightRadius: '6px',
      },
    },
    '& div.Mui-odd.MuiDataGrid-row.Mui-selected': {
      backgroundColor: '#4485BE',
      '&:last-child': {
        borderBottomLeftRadius: '6px',
        borderBottomRightRadius: '6px',
      },
    },
  },

}));

const RepairItemsScanned: React.FC<IRepairItemsScannedProps> = (
  {
    openedBox, mutate, manuallyAddedRepairs, setManuallyAddedRepairs,
  },
) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedRepairId, setSelectedRepairId] = React.useState<string | null>(null);
  const [showEmptyBagDialog, setShowEmptyBagDialog] = React.useState<boolean>(false);
  const [savingRepairItems, setSavingRepairItems] = React.useState<boolean>(false);
  const [showManagerOverrideModal, setShowManagerOverrideModal] = React.useState<boolean>(false);
  const [managerCode, setManagerCode] = React.useState<string>('');
  const [repairIndexToDelete, setRepairIndexToDelete] = React.useState<number>();

  const { enqueueSnackbar } = useSnackbar();

  // action menu functions
  const handleVertIconClick = (event: React.MouseEvent<HTMLButtonElement>, repairId: string) => {
    if (!anchorEl) {
      setSelectedRepairId(repairId);
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCloseMenu = () => {
    setSelectedRepairId(null);
    setAnchorEl(null);
  };

  const handleMarkAsNotOnManifest = (repairId: string) => {
    axios.post('/opener/markRepairNotOnManifest', {
      boxId: openedBox.boxId,
      repairId,
    })
      .then(() => {
        mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
        enqueueSnackbar('Item set as not on manifest', { variant: 'success' });
      })
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      });

    handleCloseMenu();
  };

  const handleMarkAsHighPriority = (repairId: string) => {
    axios.post('/opener/markRepairHighPriority', {
      repairId,
      trackingNumber: openedBox.trackingNumber,
    })
      .then(() => {
        enqueueSnackbar('Item set as high priority', { variant: 'success' });
        mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
      })
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      });

    handleCloseMenu();
  };

  const handleMarkBagAsEmpty = (repairId: string) => {
    setSelectedRepairId(repairId);
    // open dialog
    setShowEmptyBagDialog(true);
    // close menu
    setAnchorEl(null);
  };

  const handleEmptyBagDialogClose = (repairId?: string, details?: string, managerCodeParam?: string) => {
    // if modal was closed with details to save
    if (details && managerCodeParam) {
      // update details
      axios.post('/opener/markBagAsEmpty', {
        repairId,
        bagEmptyDetails: details,
        managerCodeParam,
      })
        .then(() => {
          enqueueSnackbar(`Bag is empty, with details: ${details}`, { variant: 'success' });
          // close dialog
          setShowEmptyBagDialog(false);
          // unselect repair
          setSelectedRepairId(null);
        })
        .catch((error: AxiosError) => {
          enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        });
    } else {
      // close dialog
      setShowEmptyBagDialog(false);
      // unselect this repair
      setSelectedRepairId(null);
    }
  };

  const displayManifestStatus = (onManifest: boolean) => {
    if (onManifest) {
      return (
        <TableCellChip icon={<CheckCircleOutlineIcon />} label="On Manifest" backgroundColor="#DAEADC" fontColor="#6CAE75" />
      );
    }

    return (
      <TableCellChip icon={<HighlightOffIcon />} label="Not On Manifest" backgroundColor="#FDECCC" fontColor="#FAB635" />
    );
  };
  const displayVertIcon = (repairId: string) => (
    <IconButton
      aria-label="more"
      aria-controls="long-menu"
      aria-haspopup="true"
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleVertIconClick(e, repairId)}
    >
      <MoreVertIcon />
    </IconButton>
  );
  const displayBarcode = (barcodeParam: string, isHighPriority: boolean) => (
    <Box flex key={`${barcodeParam}-${isHighPriority}`}>
      {barcodeParam}
      {isHighPriority && (
        <Box clone mx={0.5} pb={0.25}>
          <ErrorIcon color="error" style={{ display: 'inline-block', verticalAlign: 'middle', fontSize: 24 }} />
        </Box>
      )}
    </Box>
  );
  const tableColumns: GridColumns = [
    {
      field: 'barcode',
      headerName: 'Barcode',
      flex: 1,
      editable: true,
      renderCell: (item) => (
        ((typeof (item.value) === 'string')
          ? (
            displayBarcode(item.value, item.row.isHighPriority || false)
          )
          : <></>
        )
      ),
    },
    {
      field: 'storeRepairNumber',
      headerName: 'Repair Number',
      flex: 1,
      editable: true,
    },
    {
      field: 'isOnManifest',
      headerName: 'Manifest Status',
      flex: 1,
      renderCell: ({ value }) => (
        ((typeof (value) === 'boolean')
          ? displayManifestStatus(value)
          : <></>
        )
      ),
    },
    {
      field: 'id',
      type: 'string',
      headerName: 'Actions',
      flex: 0.5,
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? displayVertIcon(value)
          : <></>
        )
      ),
    },
  ];

  const tableRows = openedBox.repairs?.map((r) => ({
    id: r.id,
    barcode: r.barcode,
    storeRepairNumber: r.storeRepairNumber,
    isOnManifest: r.isOnManifest,
    isHighPriority: r.isHighPriority,
  }));

  function useApiRef() {
    const apiRef = React.useRef<GridApi>();
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const _tableColumns = React.useMemo(
      () => tableColumns.concat({
        field: '__HIDDEN__',
        width: 0,
        renderCell: ({ api }) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          apiRef.current = api;
          return null;
        },
      }),
      [],
    );
    return { apiRef, columns: _tableColumns };
  }

  const { apiRef, columns } = useApiRef();

  const addNewManualAddRow = (rowIndex: number) => {
    setManuallyAddedRepairs(
      [...manuallyAddedRepairs, { barcode: '', storeRepairNumber: '', id: rowIndex }],
    );
  };

  const handleEditCellChangeCommitted = (params: GridCellEditCommitParams) => {
    // if a value has been entered
    if (params.value && typeof params.value === 'string'
      && typeof params.id === 'number') {
      const updatedManuallyAddedRepairs = manuallyAddedRepairs;
      // add value to appropriate index and attribute
      // if we just left the barcode field
      if (params.field === 'barcode') {
        updatedManuallyAddedRepairs[params.id][params.field] = params.value;
        setManuallyAddedRepairs([...updatedManuallyAddedRepairs]);
      } else if (params.field === 'storeRepairNumber') {
        updatedManuallyAddedRepairs[params.id][params.field] = params.value;
        if (updatedManuallyAddedRepairs.filter((r) => r.storeRepairNumber.length === 0 && r.barcode.length === 0).length === 0) {
          addNewManualAddRow(([...updatedManuallyAddedRepairs].sort((a, b) => b.id - a.id)[0].id) + 1);
        } else {
          setManuallyAddedRepairs([...updatedManuallyAddedRepairs]);
        }
      }
    }
  };

  const deleteManuallyAddedRepairRow = (index: number) => {
    const repairsToAdd = [...manuallyAddedRepairs];
    repairsToAdd.splice(index, 1);
    setManuallyAddedRepairs(repairsToAdd);
    setRepairIndexToDelete(undefined);
  };

  const handleClearIconClick = (index: number) => {
    if (manuallyAddedRepairs[index].storeRepairNumber.length > 0) {
      setRepairIndexToDelete(index);
      setShowManagerOverrideModal(true);
    } else {
      deleteManuallyAddedRepairRow(index);
    }
  };

  const displayClearIcon = (params: GridCellParams) => (
    <ClearIcon
      onClick={() => (
        typeof params.id === 'number'
          ? handleClearIconClick(params.id)
          : handleClearIconClick(parseInt(params.id, 10))
      )}
      htmlColor="#EF5350"
    />
  );

  const manualAddApiRef = React.useRef<GridApi>();

  const manualAddColumns: GridColDef[] = [
    { field: 'id', type: 'number', hide: true },
    {
      field: 'barcode',
      headerName: 'Barcode',
      flex: 1,
      editable: true,
      renderCell: ({ value }) => (
        (value === ''
          ? <Typography tabIndex={0} variant="body2" color="textSecondary">{'Barcode'}</Typography>
          : (
            <Typography tabIndex={0} variant="body2">
              {value}
            </Typography>
          ))
      ),
    },
    {
      field: 'storeRepairNumber',
      headerName: 'Repair Number',
      flex: 1,
      editable: true,
      renderCell: ({ value }) => (
        (value === ''
          ? <Typography tabIndex={0} variant="body2" color="textSecondary">{'Repair Number'}</Typography>
          : (
            <Typography tabIndex={0} variant="body2">
              {value}
            </Typography>
          ))
      ),
    },
    {
      field: 'clear',
      type: 'number',
      flex: 2,
      renderCell: (params: GridCellParams) => (displayClearIcon(params)),
    },
    {
      field: '__HIDDEN__',
      width: 0,
      renderCell: ({ api }) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        manualAddApiRef.current = api;
        return null;
      },
    },
  ];
  const manualAddRows = manuallyAddedRepairs.map((mar, i: number) => ({
    barcode: mar.barcode,
    storeRepairNumber: mar.storeRepairNumber,
    id: i,
  }));

  const handleSaveManualAdds = debounce(
    () => {
      setSavingRepairItems(true);
      // remove empty rows
      const addRepairs = manuallyAddedRepairs
        .filter((mar) => (mar.barcode.length > 0 || mar.storeRepairNumber.length > 0));
      if (addRepairs.length === 0) {
        enqueueSnackbar('Cannot save, trying to send empty repair list.', { persist: false, variant: 'error' });
        setSavingRepairItems(false);
      } else if (addRepairs.filter((mar) => (mar.barcode.length === 0 || mar.storeRepairNumber.length === 0)).length > 0) {
        enqueueSnackbar('Cannot save, some repairs are still missing information.', { persist: false, variant: 'error' });
        setSavingRepairItems(false);
      } else if (
        new Set(addRepairs.map((r) => r.barcode)).size !== addRepairs.length
      ) {
        enqueueSnackbar('Cannot save, there are duplicate barcodes.', { persist: false, variant: 'error' });
        setSavingRepairItems(false);
      } else {
        axios.post('/opener/addRepairs', {
          boxId: openedBox.boxId,
          repairs: addRepairs,
        })
          .then(() => {
            setSavingRepairItems(false);
            mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
            // clear adds
            setManuallyAddedRepairs([]);
            enqueueSnackbar('Repairs Saved', { variant: 'success' });
          })
          .catch((error: AxiosError) => {
            setSavingRepairItems(false);
            enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
          });
      }
    }, 500,
  );

  const handleManualAddBarcodeScan = (scannedBarcodeParameter:string) => {
    if (manualAddApiRef.current !== undefined && scannedBarcodeParameter !== '') {
      manualAddApiRef.current.getRowModels().forEach((value) => {
        if (value.storeRepairNumber === scannedBarcodeParameter
          && value.barcode === ''
          && value.barcode !== scannedBarcodeParameter
        ) {
          const rowId = +value.id;
          if (manualAddApiRef.current !== undefined) {
            manualAddApiRef.current.selectRow(rowId, true);
            manualAddApiRef.current.getRowElement(rowId)?.scrollIntoView({ block: 'center' });
            manualAddApiRef.current.updateRows(
              [
                { id: rowId, storeRepairNumber: scannedBarcodeParameter },
              ],
            );
            manuallyAddedRepairs[rowId].storeRepairNumber = String(scannedBarcodeParameter);
          }
        } else if (value.storeRepairNumber === scannedBarcodeParameter && value.barcode !== '') {
          enqueueSnackbar(`Store Repair Number ${scannedBarcodeParameter} has already been scanned`, { persist: false, variant: 'error' });
        }
      });
      if (manualAddApiRef.current.getSelectedRows().size === 1) {
        manualAddApiRef.current.getSelectedRows().forEach((value) => {
          if (value.storeRepairNumber !== scannedBarcodeParameter) {
            const rowId = +value.id;
            if (manualAddApiRef.current !== undefined) {
              manualAddApiRef.current.updateRows(
                [
                  { id: rowId, barcode: scannedBarcodeParameter },
                  { id: rowId + 1, barcode: '', storeRepairNumber: '' },
                ],
              );
              manualAddApiRef.current.selectRow(rowId, false);
              manuallyAddedRepairs[rowId].barcode = String(scannedBarcodeParameter);
              if (manuallyAddedRepairs.filter((r) => r.storeRepairNumber.length === 0 && r.barcode.length === 0).length === 0) {
                addNewManualAddRow(rowId + 1);
              } else {
                setManuallyAddedRepairs([...manuallyAddedRepairs]);
              }
            }
          } else {
            enqueueSnackbar(`${scannedBarcodeParameter} already has been scanned`, { persist: false, variant: 'error' });
          }
        });
      } else if (manuallyAddedRepairs.length === 0 || (manuallyAddedRepairs?.findIndex((r) => r.storeRepairNumber === scannedBarcodeParameter) === -1)) {
        let newRowId = manuallyAddedRepairs.find((r) => r.storeRepairNumber.length === 0 && r.barcode.length === 0)?.id ?? 0;

        if (newRowId === 0 && manuallyAddedRepairs.filter((mar) => (mar.barcode === '' && mar.storeRepairNumber === '')).length === 0) {
          newRowId = Math.max(...manuallyAddedRepairs.map((r) => r.id)) + 1;
        }

        manualAddApiRef.current.updateRows(
          [
            { id: newRowId, barcode: '', storeRepairNumber: scannedBarcodeParameter },
          ],
        );
        manualAddApiRef.current.selectRow(newRowId, true);
        const tempMAR = manuallyAddedRepairs;
        const arrayIndex = tempMAR.findIndex((r) => r.id === newRowId);
        if (arrayIndex === -1) {
          setManuallyAddedRepairs(
            [...manuallyAddedRepairs, { barcode: '', storeRepairNumber: scannedBarcodeParameter, id: newRowId }],
          );
        } else {
          tempMAR[arrayIndex].storeRepairNumber = String(scannedBarcodeParameter);
          setManuallyAddedRepairs([...tempMAR]);
        }
      }
    }
  };

  const deselectManualAddDatagrid = () => {
    if (manualAddApiRef.current !== undefined) {
      const selectedRows = manualAddApiRef.current.getSelectedRows();
      if (selectedRows) {
        selectedRows.forEach(
          (row) => {
            if (manualAddApiRef.current !== undefined) {
              manualAddApiRef.current.selectRow(row.id, false);
            }
          },
        );
      }
    }
  };

  const deselectDatagrid = () => {
    if (apiRef.current !== undefined) {
      const selectedRows = apiRef.current.getSelectedRows();
      if (selectedRows) {
        selectedRows.forEach(
          (row) => {
            if (apiRef.current !== undefined) {
              apiRef.current.selectRow(row.id, false);
            }
          },
        );
      }
    }
  };

  let barcodeBuffer = '';

  const handleBarcodeScannerInput = (e: KeyboardEvent) => {
    if (e.key !== 'Shift') {
      barcodeBuffer = `${barcodeBuffer}${e.key}`;
      if (barcodeBuffer.toLowerCase().includes('in:')) {
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('{*|}')) {
        if (manuallyAddedRepairs?.length > 0) {
          handleManualAddBarcodeScan(barcodeBuffer.replace('{*|}', ''));
        }
        barcodeBuffer = '';
      }
      if (barcodeBuffer.toLowerCase().includes('out:')) {
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('<>??<>')) {
        barcodeBuffer = '';
      }
    }
  };

  useEventListener('keydown', handleBarcodeScannerInput);

  const [editRowsModel, setEditRowsModel] = React.useState<GridEditRowsModel>({});

  const handleEditRowsModelChange = (newModel: GridEditRowsModel) => {
    const updatedModel = { ...newModel };
    Object.keys(updatedModel).forEach((id) => {
      if (updatedModel[id].barcode) {
        const isValid = /^\d{10}$/.test(String(updatedModel[id].barcode.value));
        updatedModel[id].barcode = { ...updatedModel[id].barcode, error: !isValid };
      }
    });
    setEditRowsModel(updatedModel);
  };

  return (
    <Box component="span">
      <Paper>
        {
      openedBox.isUnopened ? (
        <Box p={2}>
          <Box display="flex">
            <Box flexGrow={1}>
              <Typography variant="h6" color="primary" className={classes.title}>{'Repair items scanned'}</Typography>
            </Box>
            <Tooltip title="Add repairs manually" placement="top">
              <IconButton
                size="small"
                edge="end"
                onClick={() => addNewManualAddRow(([...manuallyAddedRepairs].sort((a, b) => b.id - a.id)[0]?.id || -1) + 1)}
              >
                <AddIcon color="primary" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box>
            {manuallyAddedRepairs?.length > 0
              && (
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <ClickAwayListener onClickAway={() => deselectManualAddDatagrid()}>
                      <DataGrid
                        rows={manualAddRows}
                        columns={manualAddColumns}
                        headerHeight={0}
                        autoHeight
                        hideFooter
                        disableColumnMenu
                        isCellEditable={() => !savingRepairItems}
                        editRowsModel={editRowsModel}
                        onEditRowsModelChange={handleEditRowsModelChange}
                        onCellEditCommit={handleEditCellChangeCommitted}
                        className={classes.tableContainer}
                      />
                    </ClickAwayListener>
                  </Grid>
                  <Grid item container alignItems="flex-end" style={{ width: '56.8%', color: '#959595' }}>
                    <Box component="span" fontStyle="italic">
                      <Typography variant="caption">{'Once information is added, save your manually added repair items'}</Typography>
                    </Box>
                  </Grid>
                  <Grid container direction="row-reverse" item>
                    <Grid item>
                      <Button
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveManualAdds(); }}
                        onClick={() => handleSaveManualAdds()}
                        fullWidth
                        variant="contained"
                        color="inherit"
                        className={classes.saveRepairButton}
                        disabled={savingRepairItems || manuallyAddedRepairs.filter((mar) => (mar.barcode.length === 0 && mar.storeRepairNumber.length > 0)).length > 0}
                      >
                        {
                        savingRepairItems
                          ? <CircularProgress size={24} />
                          : (
                            <>
                              <Typography
                                variant="subtitle2"
                                color="inherit"
                                style={{
                                  marginRight: '0.5em',
                                }}
                              >
                                {'Save Repair Items  '}
                              </Typography>
                              <SaveIcon
                                color="inherit"
                                style={{
                                  marginBottom: '.1em',
                                  fontSize: '1.25em',
                                }}
                              />
                            </>
                          )
                        }
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              )}
          </Box>
        </Box>
      ) : (
        <ClickAwayListener onClickAway={() => deselectDatagrid()}>
          <DataGrid
            rows={tableRows || []}
            columns={columns}
            className={classes.noFocus}
            autoHeight
            hideFooter
            isCellEditable={() => false}
            disableColumnMenu
          />
        </ClickAwayListener>
      )
}
        { selectedRepairId && anchorEl && (
          <RepairActionMenu
            repairId={selectedRepairId}
            anchorEl={anchorEl}
            handleMarkEmpty={handleMarkBagAsEmpty}
            handleMarkUnlisted={handleMarkAsNotOnManifest}
            handleClose={handleCloseMenu}
            handleMarkAsHighPriority={
              (openedBox.isRush && openedBox.repairs.find((r) => r.id === selectedRepairId)?.isHighPriority === false) ? handleMarkAsHighPriority : null
            }
          />
        )}

        {selectedRepairId && (
          <EmptyBagDialog
            repairId={selectedRepairId}
            showModal={showEmptyBagDialog}
            onClose={handleEmptyBagDialogClose}
          />
        )}

        {showManagerOverrideModal
        && typeof repairIndexToDelete === 'number' && (
          <ManagerOverrideModal
            open={showManagerOverrideModal}
            onClose={() => {
              setShowManagerOverrideModal(false);
              setRepairIndexToDelete(undefined);
              setManagerCode('');
            }}
            managerCode={managerCode}
            setManagerCode={setManagerCode}
            handleManagerOverride={() => {
              deleteManuallyAddedRepairRow(repairIndexToDelete);
              setManagerCode('');
              setShowManagerOverrideModal(false);
            }}
          />
        )}

      </Paper>
    </Box>
  );
};

export default RepairItemsScanned;
